import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n/LangContext";
import { MaskLines, Eyebrow, Reveal } from "../components/Reveal";

const StateMeter = ({ progress, labels }) => {
  const w = useTransform(progress, [0.1, 0.7], ["0%", "100%"]);
  return (
    <div className="mt-10 max-w-sm" data-testid="clarity-state-meter">
      <div className="flex justify-between text-xs text-noor-mist">
        <span>{labels[0]}</span>
        <span className="text-noor-champagne">{labels[1]}</span>
      </div>
      <div className="mt-3 h-px w-full bg-noor-pearl/15">
        <motion.div style={{ width: w }} className="h-px bg-gradient-to-r from-noor-gold to-noor-champagne rtl:bg-gradient-to-l" />
      </div>
    </div>
  );
};

export const Clarity = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 50%", "end 70%"] });
  return (
    <section ref={ref} id="clarity" data-core data-testid="clarity-section" className="relative z-[2] min-h-[170vh] py-28 md:py-40">
      <div className="mx-auto grid max-w-[1440px] px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5 lg:col-start-8">
          <div className="lg:sticky lg:top-32">
            <div className="glass-soft rounded-[28px] p-7 sm:p-9 lg:p-11">
              <Eyebrow>{t.clarity.eyebrow}</Eyebrow>
              <MaskLines lines={t.clarity.lines} className="display-lg mt-6 font-display text-noor-pearl" lineClassName="italic-accent text-noor-champagne" testId="clarity-headline" />
              <Reveal as="p" className="mt-6 max-w-md text-[15px] leading-relaxed text-noor-mist">{t.clarity.intro}</Reveal>
              <StateMeter progress={scrollYProgress} labels={t.clarity.states} />
            </div>
          </div>
          <ol className="mt-[40vh] flex flex-col gap-[22vh] lg:mt-[45vh]">
            {t.clarity.items.map((it, i) => (
              <Reveal as="li" key={it.n} delay={0.05} data-testid={`clarity-item-${i}`} className="glass-soft rounded-[28px] p-7 md:p-9">
                <span className="font-brand text-sm text-noor-gold">{it.n}</span>
                <h3 className="mt-4 font-display text-3xl text-noor-pearl md:text-4xl">{it.title}</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-noor-mist">{it.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
